"use client";

import { useEffect } from "react";
import { useSelector } from "react-redux";
import useRouter from "@stadium/hooks/lib/use-custom-route";
import dynamic from "next/dynamic";

import { useComponentLifecycle } from "@stadium/hooks/lib/use-component-lifecycle";

import { useStoreContext } from "@components/store/provider";
import StoreProductsListingContainer from "@components/store/products/listing";
import BottomIconHideWrapper from "@stadium/shared/src/components/bottom-icon-hide-wrapper";

const KitItemsProvider = dynamic(() => import("@contexts/kit-items"));
const KitProductsListing = dynamic(() => import("@components/store/products/kit-listing"));
const KitLayout = dynamic(() => import("@components/layout/kit"));

import { getCountry } from "@selectors/country";
import { useCreateShopContext } from "@contexts/create-shop-context";
import { getStore } from "@selectors/store";

import { getStoreBasePath } from "@constants/routes";
import { KIT_COUNTRIES_ISO_LIST } from "@constants/country";
import { useCreateSwagContext } from "@contexts/create-swag-context";
import { BREADCRUMB_COLLECTION, BREADCRUMB_LISTING } from "@constants/breadcrumbs";

const DEFAULT_OBJ = {};

const getListingPageType = ({ isKitShop, menuIdentifier }) => {
  if (isKitShop) return "kit";
  if (menuIdentifier) return "collection";
  return "listing";
};

const StoreProductsListing = () => {
  const router = useRouter();
  const { isSwagCreationShopFlow } = useCreateSwagContext();
  const {
    setPageState,
    resetPageState,
    storeNumber,
    category,
    subCategory,
    countryCode,
    lang,
    storeCode,
    isNewRoute,
    workspaceSlug,
    menuIdentifier,
  } = useStoreContext();

  useComponentLifecycle({ onUnmount: resetPageState });

  const store = useSelector(getStore);
  const country = useSelector(getCountry);

  const { isLockerShop, isKitShop } = store || DEFAULT_OBJ;
  const { isNewShopCreationFlow } = useCreateShopContext();

  const listingPageType = getListingPageType({ isKitShop, menuIdentifier });

  useEffect(() => {
    const breadcrumbPageType = menuIdentifier ? BREADCRUMB_COLLECTION : BREADCRUMB_LISTING;

    if (!isNewShopCreationFlow) {
      setPageState((state) => ({
        ...state,
        withCatalogMode: true,
        showSendItemIcon: true,
        pageType: listingPageType,
        breadcrumbPageType,
      }));
      return;
    }
    setPageState((state) => ({
      ...state,
      withCart: false,
      withSearch: false,
      showRewardPoints: false,
      withCatalogMode: true,
      showSendItemIcon: true,
      showFooter: false,
      pageType: listingPageType,
      breadcrumbPageType,
    }));
  }, [menuIdentifier, isNewShopCreationFlow, isKitShop, setPageState]);

  useEffect(() => {
    //url redirect to valid countryCode in StoreLayout
    if (isLockerShop) return;

    const storeBasePath = getStoreBasePath({
      countryCode,
      lang,
      storeCode,
      storeNumber,
      isNewRoute,
      workspaceSlug,
    });

    const hasBundleSupportedCountry = KIT_COUNTRIES_ISO_LIST.includes(countryCode?.toUpperCase());

    if ((isKitShop && !hasBundleSupportedCountry) || isSwagCreationShopFlow) {
      return;
    }

    if (country?.iso && country?.iso?.toLowerCase() !== countryCode?.toLowerCase()) {
      router.push(`${storeBasePath}/menu`);
    }
  }, [countryCode, country?.iso, isKitShop, isLockerShop, workspaceSlug]);

  return (
    <BottomIconHideWrapper>
      {isKitShop ? (
        <KitItemsProvider>
          <KitLayout>
            <KitProductsListing category={category} subCategory={subCategory} />
          </KitLayout>
        </KitItemsProvider>
      ) : (
        <StoreProductsListingContainer
          category={category}
          slug={storeCode}
          subCategory={subCategory}
        />
      )}
    </BottomIconHideWrapper>
  );
};

export default StoreProductsListing;
